import { Link, createFileRoute } from '@tanstack/react-router';
import { AuctionCard } from '@/components/auction';
import { SectionHeader, LivePulse, AuctionCardSkeleton } from '@/components/ui';
import { useOpenAuctions, toAuctionListItem } from '@/hooks/useAuctions';

export const Route = createFileRoute('/')({
  component: AuctionListPage,
});

// ─── Page ─────────────────────────────────────────────────────────────────────

function AuctionListPage() {
  const { results, status, loadMore } = useOpenAuctions(20);

  const items = results.map(toAuctionListItem);
  const totalBids = items.reduce((sum, i) => sum + i.totalBids, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Hero Header ── */}
      <div className="border-b border-iron-800 bg-iron-950/40 backdrop-blur-sm">
        <div className="container mx-auto max-w-6xl px-8 py-16 flex flex-col items-center text-center">
          <div className="w-px h-12 bg-secondary/60 mb-6 shadow-[0_0_12px_rgba(241,198,41,0.4)]" />
          <h1 className="text-5xl md:text-6xl tracking-[0.35em] text-iron-100 mb-4">
            The Grand Auction
          </h1>
          <div className="flex items-center gap-4 w-full justify-center max-w-md">
            <div className="h-px grow bg-iron-800" />
            <p className="text-[10px] tracking-[0.4em] text-secondary uppercase font-bold shrink-0">
              Finest Cards of the Realm
            </p>
            <div className="h-px grow bg-iron-800" />
          </div>
          <p className="mt-6 text-iron-400 font-body text-lg italic max-w-lg">
            "They who hold the rarest cards, hold the power."
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4">
            <Link
              to="/cards/new"
              className="bg-secondary px-8 py-4 font-epic text-sm uppercase tracking-[0.32em] text-iron-950 transition-all hover:brightness-110"
            >
              Submit Trading Card
            </Link>
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-iron-500">
              Public card listings are now open
            </span>
          </div>
        </div>
      </div>

      {/* ── Live Banner ── */}
      <div className="bg-primary/5 border-b border-primary/10 py-2">
        <div className="container mx-auto max-w-6xl px-8 flex items-center gap-3">
          <LivePulse variant="blood" size="md" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-primary/80">
            {status === 'LoadingFirstPage'
              ? 'Loading auctions…'
              : `Live Auction — ${totalBids} active bids`}
          </span>
        </div>
      </div>

      {/* ── Auction Grid ── */}
      <div className="container mx-auto max-w-6xl px-8 py-16">
        <SectionHeader
          title="Active Listings"
          trailing={status !== 'LoadingFirstPage' ? `${items.length} items` : undefined}
          className="mb-12"
        />

        {/* Loading skeleton */}
        {status === 'LoadingFirstPage' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <AuctionCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {status !== 'LoadingFirstPage' && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
            <div className="w-px h-12 bg-iron-700" />
            <p className="text-iron-500 font-body italic text-lg">
              No auctions are currently open. Check back soon, adventurer.
            </p>
          </div>
        )}

        {/* Grid */}
        {items.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <AuctionCard key={item.id} item={item} />
              ))}
            </div>

            {/* Load more */}
            {status === 'CanLoadMore' && (
              <div className="flex justify-center mt-12">
                <button
                  type="button"
                  onClick={() => loadMore(20)}
                  className="border border-iron-700 text-iron-300 text-[10px] tracking-[0.3em] uppercase font-bold px-8 py-4 hover:border-secondary/50 hover:text-secondary transition-colors"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
